import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { bookingSchema } from '../../utils/validationSchemas';
import { useBookingStore } from '../../store/bookingStore';
import { useQuery } from '@tanstack/react-query';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { createOrUpdateClientAndBook } from '../../firebase/clients';
import { checkTimeSlotAvailability } from '../../services/availability';
import { useAvailabilityQuery } from '../../hooks/useAvailabilityQuery';
import { formatISO, formatDateBR } from '../../utils/dateUtils';
import ServiceSelection from './steps/ServiceSelection';
import DateTimeSelection from './steps/DateTimeSelection';
import ClientForm from './steps/ClientForm';
import ProgressIndicator from './ProgressIndicator';
import SalonHeader from './shared/SalonHeader';
import SuccessMessage from './shared/SuccessMessage';
import LoadingIndicator from './shared/LoadingIndicator';

// Função para buscar configuração do salão de forma otimizada
const fetchSalonConfig = async (salonId) => {
  try {
    // Buscar apenas os dados do salão, sem agendamentos ou outros dados desnecessários
    const salonDoc = await getDoc(doc(db, `salons/${salonId}`));
    
    if (!salonDoc.exists()) {
      throw new Error('Salão não encontrado');
    }
    
    const salonData = {
      id: salonDoc.id,
      ...salonDoc.data()
    };
    
    // Garantir que a estrutura de avisos exista
    if (!salonData.customNotices) {
      salonData.customNotices = {
        pricingNotice: { 
          enabled: true, 
          message: "Os preços podem variar de acordo com o profissional selecionado",
          variant: "gray"
        },
        generalNotice: { 
          enabled: false, 
          message: "", 
          variant: "gray", 
          displayLocation: "all" 
        }
      };
    } else {
      // Garantir que o campo variant exista no pricingNotice
      if (salonData.customNotices.pricingNotice && !salonData.customNotices.pricingNotice.variant) {
        salonData.customNotices.pricingNotice.variant = "gray";
      }
    }
    
    return salonData;
  } catch (error) {
    console.error('Erro ao buscar configuração do salão:', error);
    throw error;
  }
};

// Função para verificar se existe pelo menos um profissional com customPrice
const checkCustomPriceExists = async (salonId) => {
  try {
    const professionalServicesRef = collection(db, `salons/${salonId}/professional_services`);
    const q = query(
      professionalServicesRef,
      where('customPrice', '!=', null),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Erro ao verificar preços personalizados:', error);
    return false;
  }
};

/**
 * Página principal de agendamento
 * Implementa o fluxo de agendamento em 3 etapas
 * Otimizada para reduzir consultas e dependências desnecessárias
 */
const BookingPage = () => {
  const { salonId } = useParams();
  
  // Buscar configuração do salão com cache de 24 horas (dados estáticos)
  const { data: salonConfig, isLoading, error } = useQuery({
    queryKey: ['salonConfig', salonId],
    queryFn: () => fetchSalonConfig(salonId),
    staleTime: 24 * 60 * 60 * 1000, // 24 horas
    enabled: !!salonId,
    retry: 2
  });
  
  // Verificar se existem profissionais com preços personalizados
  const { data: hasCustomPrices } = useQuery({
    queryKey: ['hasCustomPrices', salonId],
    queryFn: () => checkCustomPriceExists(salonId),
    staleTime: 60 * 60 * 1000, // 1 hora
    enabled: !!salonId,
    retry: 1
  });
  
  // Estados do Zustand store - não depende de contextos externos
  const step = useBookingStore((state) => state.step);
  const nextStep = useBookingStore((state) => state.nextStep);
  const prevStep = useBookingStore((state) => state.prevStep);
  const resetBooking = useBookingStore((state) => state.resetBooking);
  
  // Form context do React Hook Form
  const methods = useForm({
    resolver: zodResolver(bookingSchema),
    mode: 'onChange',
    defaultValues: {
      services: [],
      date: null,
      time: null,
      professional: null,
      client: {
        name: '',
        phone: '',
        email: '',
        notes: ''
      }
    }
  });
  
  // Estado de sucesso após finalização do agendamento
  const [success, setSuccess] = useState(false);
  // Estado de loading durante o envio
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Estado para erros
  const [submitError, setSubmitError] = useState(null);
  
  // Obter apenas o hook de invalidação de cache que será necessário
  const { invalidateAvailability } = useAvailabilityQuery(salonId);
  
  // Função para processar o agendamento - otimizada para verificar disponibilidade específica
  const handleSubmitBooking = async (data) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      
      // Extrair informações para verificação de disponibilidade
      const dateStr = formatISO(data.date);
      
      const professionalId = typeof data.professional === 'object' 
        ? data.professional.id 
        : data.professional;
      
      const serviceIds = data.services.map(service => 
        typeof service === 'object' ? service.id : service
      );
      
      // Verificar disponibilidade final apenas para o horário/profissional específico
      const availability = await checkTimeSlotAvailability(
        salonId,
        dateStr,
        data.time,
        professionalId,
        serviceIds
      );
      
      // Se não estiver disponível, mostrar erro e não continuar
      if (!availability.available) {
        let errorMessage = "Este horário não está mais disponível.";
        
        if (availability.hasBlockingConflicts) {
          errorMessage = "Este horário foi bloqueado. Por favor, escolha outro horário.";
        } else if (availability.hasAppointmentConflicts) {
          errorMessage = "Este horário foi reservado por outro cliente. Por favor, escolha outro horário.";
        }
        
        setSubmitError(errorMessage);
        setIsSubmitting(false);
        return false;
      }
      
      // Reestruturar dados para formato esperado
      const appointmentData = {
        cliente: {
          nome: data.client.name,
          celular: data.client.phone,
          email: data.client.email || "",
        },
        observacoes: data.client.notes || "",
        data: dateStr,
        dataFormatada: formatDateBR(data.date),
        horario: data.time,
        profissional: professionalId,
        servicos: serviceIds,
        servicosDetalhes: data.services.map(service => {
          if (typeof service !== 'object') return null;
          return {
            id: service.id,
            nome: service.nome,
            preco: service.preco || 0,
            duracao: service.duracao || 0
          };
        }).filter(Boolean),
        origem: 'cliente',
        deviceType: navigator.userAgent.match(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i) 
          ? 'mobile' : 'desktop'
      };
      
      // Usar createOrUpdateClientAndBook
      const result = await createOrUpdateClientAndBook(salonId, appointmentData);
      
      if (!result.success) {
        throw new Error(result.error || "Erro ao criar agendamento");
      }
      
      // Após sucesso, invalidar apenas o cache de disponibilidade para esta data
      try {
        await invalidateAvailability(dateStr);
      } catch (error) {
        console.error('Erro ao invalidar cache:', error);
        // Não interromper fluxo principal se isso falhar
      }
      
      // Marcar como sucesso
      setSuccess(true);
      
      // Resetar booking após um tempo
      setTimeout(() => {
        setSuccess(false);
        resetBooking();
        methods.reset();
      }, 8000);
      
      return true;
    } catch (error) {
      console.error('Erro ao enviar agendamento:', error);
      setSubmitError(error.message || 'Ocorreu um erro ao criar o agendamento');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Extrair as cores configuradas do salão
  const primaryColor = salonConfig?.primaryColor || '#4F46E5';
  const secondaryColor = salonConfig?.secondaryColor || '#F9FAFB';
  const textColor = salonConfig?.textColor || '#111827';
  
  // Exibir loading enquanto carrega configuração do salão
  if (isLoading) return <LoadingIndicator />;
  
  // Exibir erro se não conseguiu carregar o salão
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-6 max-w-md w-full">
          <h2 className="text-2xl font-semibold text-red-600 mb-2">Erro</h2>
          <p className="text-gray-700">
            Não foi possível carregar as informações do salão. Tente novamente mais tarde.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="min-h-screen"
      style={{ 
        fontFamily: salonConfig?.fontFamily || 'sans-serif',
        backgroundColor: secondaryColor,
        color: textColor 
      }}
    >
      <SalonHeader salonConfig={salonConfig} />
      
      <main className="container mx-auto py-4 px-4 max-w-md">
        <FormProvider {...methods}>
          {success ? (
            <SuccessMessage 
              onNewBooking={() => setSuccess(false)} 
              salonConfig={salonConfig}
            />
          ) : (
            <>
              <ProgressIndicator 
                currentStep={step} 
                primaryColor={primaryColor} 
                simpleMode={false}
              />
              
              {step === 1 && (
                <ServiceSelection 
                  salonId={salonId}
                  salonConfig={salonConfig}
                  onContinue={nextStep}
                  isAdmin={false}
                  hasCustomPrices={hasCustomPrices}
                />
              )}
              
              {step === 2 && (
                <DateTimeSelection 
                  salonId={salonId}
                  salonConfig={salonConfig}
                  onContinue={nextStep}
                  onBack={prevStep}
                  isAdmin={false}
                />
              )}
              
              {step === 3 && (
                <ClientForm 
                  salonId={salonId}
                  salonConfig={salonConfig}
                  onBack={prevStep}
                  onSubmit={handleSubmitBooking}
                  isSubmitting={isSubmitting}
                  submitError={submitError}
                  isAdmin={false}
                />
              )}
            </>
          )}
        </FormProvider>
      </main>
    </div>
  );
};

export default BookingPage; 